import { useEffect, useRef, useState } from 'react';
import { AugmentedRealityType } from '../../../types/augmentedReality';
import TopBar from 'components/AugmentedReality/Structure/TopBar';
import BottomBar from 'components/AugmentedReality/Calibrate/BottomBar';
import {
  SCCalibrateContainer,
  SCOverlayImage,
  SCVideo,
  SCCalibrateZoom,
  SCCalibrateSpan
} from 'components/AugmentedReality/Calibrate/styled'
import useCamera from 'components/AugmentedReality/hooks/useCamera';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface CalibrateFrameProps {
  ra: AugmentedRealityType;
  camera: ReturnType<typeof useCamera>;
  onNext: () => void;
  onPrevious: () => void;
  overlayTransform: { x: number; y: number; scale: number };
  onOverlayTransformChange: (transform: { x: number; y: number; scale: number }) => void;
}

const CalibrateFrame = ({ ra, camera, onNext, onPrevious, overlayTransform, onOverlayTransformChange }: CalibrateFrameProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const zoomStep = 0.1;
  const [zoomLevel, setZoomLevel] = useState(0.5)
  const [zoomMax, setZoomMax] = useState(3)
  const [zoomMin, setZoomMin] = useState(0.5)
  const [canZoom, setCanZoom] = useState<boolean>(false)

  useEffect(() => {
    const initialCameraMode = ra?.config?.camera === 'frontal' ? 'user' : 'environment';
    camera.startCamera(initialCameraMode);
  }, [ra]);

  useEffect(() => {
    if (videoRef.current && camera.stream) {
      videoRef.current.srcObject = camera.stream;
      const zoom = camera.getZoomInfo();
      if (zoom?.zoom) {
        setZoomLevel(zoom.zoom?.min ? zoom.zoom.min : 1)
        setZoomMin(zoom.zoom?.min ? zoom.zoom.min : 1)
        setZoomMax(zoom.zoom?.max ? zoom.zoom.max : 5)
        setCanZoom(true);
      }
    }
  }, [camera.stream, camera.getZoomInfo]);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + zoomStep, zoomMax);
    setZoomLevel(newZoom);
    camera.zoomCamera(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - zoomStep, zoomMin);
    setZoomLevel(newZoom);
    camera.zoomCamera(newZoom);
  };

  const handleSwitchCamera = async () => {
    await camera.switchCamera();
  };

  return (
    <SCCalibrateContainer>
      <TopBar
        label={ra.calibrate.title !== '' ? ra.calibrate.title : 'Alignez les lignes avec le bâtiment'}
      />
      <SCVideo ref={videoRef} autoPlay playsInline muted/>

      <TransformWrapper
        initialPositionX={overlayTransform.x}
        initialPositionY={overlayTransform.y}
        initialScale={overlayTransform.scale}
        onTransformed={(_ref, state) => {
          onOverlayTransformChange({ x: state.positionX, y: state.positionY, scale: state.scale });
        }}
        limitToBounds={false}
        doubleClick={{ disabled: true }}
        minScale={0.1}
        maxScale={10}
      >
        <TransformComponent
          wrapperStyle={{ position: 'absolute', inset: 0, zIndex: 3, overflow: 'hidden', width: '100%', height: '100%', touchAction: 'none' }}
          contentStyle={{ width: '100%', height: '100%', position: 'relative', display: 'block' }}
        >
          <SCOverlayImage crossOrigin="anonymous" src={ra.calibrate.image} alt="calibrate overlay" $size={300} />
        </TransformComponent>
      </TransformWrapper>

      {canZoom && (
        <SCCalibrateZoom>
          <SCCalibrateSpan>Niveau de zoom</SCCalibrateSpan>
          <button onClick={handleZoomOut}>➖</button>
          <SCCalibrateSpan style={{ margin: '0 8px' }}>{zoomLevel.toFixed(1)}x</SCCalibrateSpan>
          <button onClick={handleZoomIn}>➕</button>
        </SCCalibrateZoom>
      )}

      <BottomBar onPrevious={onPrevious} onNext={onNext} switchCamera={handleSwitchCamera} />
    </SCCalibrateContainer>
  )
};

export default CalibrateFrame;